import React from 'react'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts'
import { MetricCard, Card, SectionTitle, Badge, EmptyState, Table, Td } from './ui'
import { AGENT_COLORS, AGENT_INITIALS, DAILY_TARGETS, WEEKLY_MEETING_TARGETS, TELESALES_AGENTS, TELEMARKETING_AGENTS, STAGE_COLORS, KPI_START_DATE, DATABASE_CONTACTS } from '../lib/constants'

// Get the Monday of a given date's week
function getWeekStart(dateStr) {
  const d = new Date(dateStr)
  const day = d.getDay()
  const diff = d.getDate() - day + (day === 0 ? -6 : 1) // Monday
  return new Date(d.setDate(diff)).toISOString().slice(0, 10)
}

// Get all week start dates, tergantung mode:
// - 'current'  : dari KPI_START_DATE (1 Sep 2026) sampai minggu berjalan.
// - 'history'  : dari call paling awal sampai minggu terakhir SEBELUM KPI_START_DATE.
// History call log tetap tersimpan; ini cuma menentukan minggu mana yang dihitung.
function getWeeks(callLogs, mode = 'current') {
  const today = new Date().toISOString().slice(0, 10)

  // Cari tanggal call paling awal.
  let earliest = today
  for (const r of callLogs) {
    if (r.call_date && r.call_date < earliest) earliest = r.call_date
  }

  let start, end
  if (mode === 'history') {
    start = getWeekStart(earliest)
    // Minggu terakhir yang berakhir sebelum KPI_START_DATE.
    const lastHistoryWeek = new Date(getWeekStart(KPI_START_DATE))
    lastHistoryWeek.setDate(lastHistoryWeek.getDate() - 7)
    end = lastHistoryWeek.toISOString().slice(0, 10)
  } else {
    if (earliest < KPI_START_DATE) earliest = KPI_START_DATE
    start = getWeekStart(earliest)
    end = getWeekStart(today)
  }

  const weeks = []
  let d = new Date(start)
  const endDate = new Date(end)
  while (d <= endDate) {
    weeks.push(d.toISOString().slice(0, 10))
    d.setDate(d.getDate() + 7)
  }
  return weeks
}

// Calculate meeting debt per agent.
// mode 'current'  -> hutang sejak 1 Sep 2026 (minggu berjalan belum dihitung).
// mode 'history'  -> total hutang dari semua minggu sebelum 1 Sep 2026.
function getMeetingDebt(callLogs, agent, mode = 'current') {
  const target = WEEKLY_MEETING_TARGETS[agent] || 3
  const weeks = getWeeks(callLogs, mode)
  const today = new Date().toISOString().slice(0, 10)
  const currentWeekStart = getWeekStart(today)

  let debt = 0
  for (const weekStart of weeks) {
    const weekEnd = new Date(new Date(weekStart).getTime() + 6 * 86400000).toISOString().slice(0, 10)
    const meetingsThisWeek = callLogs.filter(r =>
      r.agent === agent && r.meeting_set === 'Yes' && r.call_date >= weekStart && r.call_date <= weekEnd
    ).length

    // Minggu berjalan belum "selesai", jadi tidak dihitung sebagai hutang.
    if (mode === 'current' && weekStart === currentWeekStart) continue

    const deficit = target - meetingsThisWeek
    if (deficit > 0) debt += deficit
  }
  return debt
}

function getMeetingsThisWeek(callLogs, agent) {
  const today = new Date().toISOString().slice(0, 10)
  const weekStart = getWeekStart(today)
  const weekEnd = new Date(new Date(weekStart).getTime() + 6 * 86400000).toISOString().slice(0, 10)
  return callLogs.filter(r =>
    r.agent === agent && r.meeting_set === 'Yes' && r.call_date >= weekStart && r.call_date <= weekEnd
  ).length
}

function weekEndOf(weekStart) {
  return new Date(new Date(weekStart).getTime() + 6 * 86400000).toISOString().slice(0, 10)
}

// Daftar semua minggu yang bisa dipilih di dashboard, mulai dari 1 Sep 2026
// sampai minggu berjalan. Dipakai untuk dropdown range mingguan.
function getSelectableWeeks() {
  const today = new Date().toISOString().slice(0, 10)
  const start = getWeekStart(KPI_START_DATE)
  const end = getWeekStart(today)
  const weeks = []
  let d = new Date(start)
  const endDate = new Date(end)
  while (d <= endDate) {
    weeks.push(d.toISOString().slice(0, 10))
    d.setDate(d.getDate() + 7)
  }
  return weeks
}

function weekLabel(weekStart) {
  const s = new Date(weekStart)
  const e = new Date(weekEndOf(weekStart))
  const fmt = (dt) => dt.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })
  return `${fmt(s)} – ${fmt(e)}`
}

// Ringkasan aktivitas 1 agent dalam range tanggal [from, to] (inklusif).
// dbContacts = gabungan database (hardcoded + supabase) untuk hitung DB input.
function getAgentSummary(callLogs, dbContacts, agent, from, to) {
  const inRange = (d) => d && d >= from && d <= to
  const calls = callLogs.filter(r => r.agent === agent && inRange(r.call_date))
  return {
    dbInput: dbContacts.filter(c => c.agent === agent).length,
    calls: calls.length,
    bantQualified: calls.filter(r => r.bant_score >= 3).length,
    passedToAM: calls.filter(r => r.passed_to_am).length,
    meetings: calls.filter(r => r.meeting_set === 'Yes').length,
  }
}

function AgentCard({ agent, callLogs, today, onNameClick, rangeFrom, rangeTo, rangeLabel }) {
  const t = DAILY_TARGETS[agent]
  const isTelemarketing = TELEMARKETING_AGENTS.includes(agent)
  const weeklyTarget = WEEKLY_MEETING_TARGETS[agent] || 0

  // Passed to AM dalam range terpilih
  const passedToAM = callLogs.filter(r =>
    r.agent === agent && r.passed_to_am && r.call_date >= rangeFrom && r.call_date <= rangeTo
  ).length

  // Telemarketing: count ALL calls (no BANT filter). Telesales: only BANT >= 3
  const todayCalls = isTelemarketing
    ? callLogs.filter(r => r.agent === agent && r.call_date === today).length
    : callLogs.filter(r => r.agent === agent && r.call_date === today && r.bant_score >= 3).length
  const meetingsThisWeek = getMeetingsThisWeek(callLogs, agent)
  const debt = isTelemarketing ? 0 : getMeetingDebt(callLogs, agent)
  const pct = t.calls > 0 ? Math.min(100, Math.round(todayCalls / t.calls * 100)) : 0
  const meetingPct = weeklyTarget > 0 ? Math.min(100, Math.round(meetingsThisWeek / weeklyTarget * 100)) : 0
  const info = AGENT_INITIALS[agent]
  const color = AGENT_COLORS[agent]

  // Total calls all-time for telemarketing
  const totalCalls = isTelemarketing ? callLogs.filter(r => r.agent === agent).length : 0

  return (
    <div style={{
      background: '#fff', border: debt > 0 ? '1px solid #FCEBEB' : '0.5px solid rgba(0,0,0,0.1)',
      borderRadius: 12, padding: '0.9rem'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <div style={{
          width: 28, height: 28, borderRadius: '50%', background: info.bg,
          color: info.text, display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 10, fontWeight: 700
        }}>{info.initials}</div>
        <button
          onClick={() => onNameClick && onNameClick(agent)}
          title="Lihat detail agent"
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#185FA5', textDecoration: 'underline', textUnderlineOffset: 2 }}>
          {agent}
        </button>
        {isTelemarketing && (
          <span style={{ marginLeft: 'auto', background: '#EEEDF9', color: '#4A4499', fontSize: 9, padding: '2px 7px', borderRadius: 99, fontWeight: 600 }}>Telemarketing</span>
        )}
        {!isTelemarketing && debt > 0 && (
          <span style={{ marginLeft: 'auto', background: '#FCEBEB', color: '#A32D2D', fontSize: 10, padding: '2px 7px', borderRadius: 99, fontWeight: 600 }}>
            {debt} meeting kurang
          </span>
        )}
      </div>

      <div style={{ fontSize: 12, display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '0.5px solid rgba(0,0,0,0.07)' }}>
        <span style={{ color: '#888' }}>{isTelemarketing ? 'Calls today' : 'Calls today (BANT≥3)'}</span>
        <span style={{ fontWeight: 600 }}>
          {todayCalls}/{t.calls}{' '}
          <Badge variant={pct >= 100 ? 'success' : 'danger'}>{pct}%</Badge>
        </span>
      </div>
      <div style={{ height: 5, background: 'rgba(0,0,0,0.07)', borderRadius: 99, margin: '4px 0 8px' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 99 }} />
      </div>

      {isTelemarketing ? (
        <>
          <div style={{ fontSize: 12, display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '0.5px solid rgba(0,0,0,0.07)' }}>
            <span style={{ color: '#888' }}>Total calls (all time)</span>
            <span style={{ fontWeight: 600 }}>{totalCalls}</span>
          </div>
        </>
      ) : (
        <>
          <div style={{ fontSize: 12, display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '0.5px solid rgba(0,0,0,0.07)' }}>
            <span style={{ color: '#888' }}>Meetings this week</span>
            <span style={{ fontWeight: 600 }}>
              {meetingsThisWeek}/{weeklyTarget}{' '}
              <Badge variant={meetingPct >= 100 ? 'success' : meetingPct >= 50 ? 'warning' : 'danger'}>{meetingPct}%</Badge>
            </span>
          </div>
          <div style={{ height: 5, background: 'rgba(0,0,0,0.07)', borderRadius: 99, margin: '4px 0 8px' }}>
            <div style={{ width: `${meetingPct}%`, height: '100%', background: meetingPct >= 100 ? '#1D9E75' : meetingPct >= 50 ? '#EF9F27' : '#E24B4A', borderRadius: 99 }} />
          </div>
          {debt > 0 && (
            <div style={{ fontSize: 11, color: '#A32D2D', background: '#FCEBEB', borderRadius: 6, padding: '5px 8px', marginTop: 4 }}>
              Kurang {debt} meeting dari minggu lalu. Total target minggu ini: {weeklyTarget + debt}
            </div>
          )}
        </>
      )}

      <div style={{ fontSize: 12, display: 'flex', justifyContent: 'space-between', padding: '4px 0', marginTop: 4 }}>
        <span style={{ color: '#888' }}>Passed to AM</span>
        <span style={{ fontWeight: 600 }}>{passedToAM}</span>
      </div>
    </div>
  )
}

export default function Dashboard({ callLogs, pipeline, reminders, dbContacts = [], isManager = false, userName = '' }) {
  const today = new Date().toISOString().slice(0, 10)
  // Role-aware: superadmin/manager sees the whole team; an agent sees only
  // themselves. Every per-agent section below is driven by `agents` (not the
  // global constant) so agents never see other agents' names or rows.
  const agents = isManager ? TELESALES_AGENTS : TELESALES_AGENTS.filter(a => a === userName)
  const [showHistory, setShowHistory] = React.useState(false)
  const kpiMode = showHistory ? 'history' : 'current'
  const kpiStartLabel = new Date(KPI_START_DATE).toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })

  // ---- Weekly range picker ----
  const selectableWeeks = getSelectableWeeks()
  const currentWeekStart = getWeekStart(today)
  // 'all' = akumulasi sejak 1 Sep 2026; selain itu, week start yang dipilih.
  const [selectedWeek, setSelectedWeek] = React.useState('all')
  const rangeFrom = selectedWeek === 'all' ? KPI_START_DATE : selectedWeek
  const rangeTo = selectedWeek === 'all' ? today : weekEndOf(selectedWeek)
  const inRange = (d) => d && d >= rangeFrom && d <= rangeTo
  const rangeLabel = selectedWeek === 'all'
    ? `Sejak ${kpiStartLabel}`
    : `Minggu ${weekLabel(selectedWeek)}`

  // Gabungan database untuk hitung "DB input" per agent (hardcoded + supabase, dedup by company)
  const dbCompanyNames = new Set(dbContacts.map(c => c.company?.trim().toLowerCase()).filter(Boolean))
  const allContacts = [...DATABASE_CONTACTS.filter(c => !(c.company && dbCompanyNames.has(c.company.trim().toLowerCase()))), ...dbContacts]

  // Agent detail popup
  const [detailAgent, setDetailAgent] = React.useState(null)

  // ---- Metrik yang mengikuti minggu terpilih ----
  const rangeCalls = callLogs.filter(r => inRange(r.call_date))
  const qualifiedCalls = rangeCalls.filter(r => r.bant_score >= 3).length
  const rangeMeetings = rangeCalls.filter(r => r.meeting_set === 'Yes').length
  const rangePassedToAM = rangeCalls.filter(r => r.passed_to_am).length
  const activeDeals = pipeline.filter(p => p.stage !== 'Closed Lost').length
  const overdueReminders = reminders.filter(r => !r.done && r.follow_up_date <= today).length

  const chartData = agents.map(a => ({
    agent: a,
    calls: callLogs.filter(r => r.agent === a && r.bant_score >= 3 && inRange(r.call_date)).length,
    fill: AGENT_COLORS[a],
  }))

  const stages = {}
  pipeline.forEach(p => {
    if (p.stage !== 'Closed Lost') stages[p.stage] = (stages[p.stage] || 0) + 1
  })

  return (
    <div>
      {/* Agent detail popup — LIFETIME (semua data sejak awal, tidak ikut filter minggu) */}
      {detailAgent && (() => {
        const s = getAgentSummary(callLogs, allContacts, detailAgent, '0000-01-01', today)
        const info = AGENT_INITIALS[detailAgent]
        const lifeSub = 'Lifetime (semua data)'
        const rows = [
          { label: 'Database di-input', value: s.dbInput, sub: lifeSub, color: '#185FA5' },
          { label: 'Leads di-call', value: s.calls, sub: lifeSub, color: '#0F6E56' },
          { label: 'Leads BANT ≥ 3', value: s.bantQualified, sub: lifeSub, color: '#854F0B' },
          { label: 'Passed to AM', value: s.passedToAM, sub: lifeSub, color: '#4A4499' },
          { label: 'Meeting set', value: s.meetings, sub: lifeSub, color: '#993556' },
        ]
        return (
          <div onClick={() => setDetailAgent(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
            <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, padding: '1.5rem', width: '100%', maxWidth: 460 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 34, height: 34, borderRadius: '50%', background: info.bg, color: info.text, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700 }}>{info.initials}</div>
                  <div style={{ fontSize: 17, fontWeight: 700 }}>{detailAgent}</div>
                </div>
                <button onClick={() => setDetailAgent(null)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#888' }}>×</button>
              </div>
              <div style={{ fontSize: 11, color: '#888', marginBottom: 16 }}>Ringkasan aktivitas — lifetime (semua data sejak awal)</div>
              {rows.map(r => (
                <div key={r.label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '0.5px solid rgba(0,0,0,0.07)' }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{r.label}</div>
                    <div style={{ fontSize: 10, color: '#aaa' }}>{r.sub}</div>
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: r.color }}>{r.value}</div>
                </div>
              ))}
            </div>
          </div>
        )
      })()}

      {/* KPI period toggle */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: '1rem', flexWrap: 'wrap' }}>
        <div style={{ fontSize: 12, color: '#666' }}>
          {showHistory
            ? <>Menampilkan <strong>KPI history</strong> (sebelum {kpiStartLabel})</>
            : <>Menampilkan <strong>KPI berjalan</strong> (mulai {kpiStartLabel})</>}
        </div>
        <button
          onClick={() => setShowHistory(v => !v)}
          style={{
            background: showHistory ? '#A32D2D' : '#fff',
            color: showHistory ? '#fff' : '#A32D2D',
            border: '1px solid #E5A5A5', borderRadius: 8, padding: '6px 14px',
            cursor: 'pointer', fontSize: 12, fontWeight: 600,
          }}
        >
          {showHistory ? `Kembali ke KPI berjalan` : `Lihat history sebelum ${kpiStartLabel}`}
        </button>
      </div>

      {/* Weekly range picker */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '1rem', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: '#444' }}>Lihat data:</span>
        <select
          value={selectedWeek}
          onChange={e => setSelectedWeek(e.target.value)}
          style={{ fontSize: 12, padding: '6px 10px', border: '0.5px solid rgba(0,0,0,0.2)', borderRadius: 8, background: '#fff', fontWeight: 600 }}>
          <option value="all">Semua (sejak {kpiStartLabel})</option>
          {selectableWeeks.slice().reverse().map(w => (
            <option key={w} value={w}>
              {weekLabel(w)}{w === currentWeekStart ? ' (minggu ini)' : ''}
            </option>
          ))}
        </select>
        <span style={{ fontSize: 11, color: '#888' }}>{rangeLabel}</span>
      </div>

      {/* Meeting debt alerts */}
      {(() => {
        const debts = agents.map(a => ({ agent: a, debt: getMeetingDebt(callLogs, a, kpiMode) })).filter(d => d.debt > 0)
        if (debts.length === 0) {
          return (
            <div style={{ background: '#E1F5EE', borderRadius: 10, padding: '12px 16px', marginBottom: '1.25rem', border: '0.5px solid #B7E4D3' }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#0F6E56' }}>
                {showHistory
                  ? `Tidak ada hutang meeting tercatat sebelum ${kpiStartLabel}.`
                  : `Semua agent on-track. Tidak ada hutang meeting sejak ${kpiStartLabel}.`}
              </div>
            </div>
          )
        }
        return (
          <div style={{ background: '#FCEBEB', borderRadius: 10, padding: '12px 16px', marginBottom: '1.25rem', border: '0.5px solid #F5C6C6' }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#A32D2D', marginBottom: 4 }}>
              {showHistory ? `Meeting KPI Alert — History (sebelum ${kpiStartLabel})` : 'Meeting KPI Alert'}
            </div>
            {debts.map(d => (
              <div key={d.agent} style={{ fontSize: 12, color: '#A32D2D', padding: '2px 0' }}>
                <strong>{d.agent}</strong> — kurang {d.debt} meeting dari minggu sebelumnya (target: {WEEKLY_MEETING_TARGETS[d.agent]}/week)
              </div>
            ))}
          </div>
        )
      })()}

      {/* Meeting Leaderboard + Debt Summary — cross-agent comparison, manager only */}
      {isManager && (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.25rem' }}>
        <Card>
          <SectionTitle>Meeting Leaderboard (sejak {kpiStartLabel})</SectionTitle>
          {(() => {
            const leaderboard = TELESALES_AGENTS.map(a => ({
              agent: a,
              total: callLogs.filter(r => r.agent === a && r.meeting_set === 'Yes' && r.call_date >= KPI_START_DATE).length,
              color: AGENT_COLORS[a],
            })).sort((a, b) => b.total - a.total)
            const max = leaderboard[0]?.total || 1
            return leaderboard.map((item, idx) => (
              <div key={item.agent} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '0.5px solid rgba(0,0,0,0.07)' }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: idx === 0 ? '#1D9E75' : '#888', minWidth: 18 }}>#{idx + 1}</span>
                <span style={{ fontSize: 12, minWidth: 65 }}>{item.agent}</span>
                <div style={{ flex: 1, height: 6, background: 'rgba(0,0,0,0.07)', borderRadius: 99, overflow: 'hidden' }}>
                  <div style={{ width: `${Math.round(item.total / max * 100)}%`, height: '100%', background: item.color, borderRadius: 99 }} />
                </div>
                <span style={{ fontSize: 12, fontWeight: 600, minWidth: 30, textAlign: 'right' }}>{item.total}</span>
              </div>
            ))
          })()}
        </Card>

        <Card>
          <SectionTitle>{showHistory ? `Meeting Kekurangan — History (sebelum ${kpiStartLabel})` : 'Meeting Kekurangan Summary'}</SectionTitle>
          <Table headers={['Agent', 'Target/Week', 'This Week', 'Kurang', 'Total Due']}>
            {TELESALES_AGENTS.map(a => {
              const target = WEEKLY_MEETING_TARGETS[a]
              const thisWeek = showHistory ? 0 : getMeetingsThisWeek(callLogs, a)
              const debt = getMeetingDebt(callLogs, a, kpiMode)
              const totalDue = target + debt
              return (
                <tr key={a}>
                  <Td style={{ fontWeight: 600 }}>{a}</Td>
                  <Td style={{ textAlign: 'center' }}>{target}</Td>
                  <Td style={{ textAlign: 'center' }}>
                    <span style={{ color: thisWeek >= target ? '#1D9E75' : '#E24B4A', fontWeight: 600 }}>{thisWeek}</span>
                  </Td>
                  <Td style={{ textAlign: 'center' }}>
                    {debt > 0
                      ? <span style={{ background: '#FCEBEB', color: '#A32D2D', padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600 }}>{debt}</span>
                      : <span style={{ color: '#1D9E75', fontWeight: 600 }}>0</span>}
                  </Td>
                  <Td style={{ textAlign: 'center', fontWeight: 600 }}>{totalDue}</Td>
                </tr>
              )
            })}
          </Table>
        </Card>
      </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10, marginBottom: '1.25rem' }}>
        <MetricCard label="Qualified calls (BANT≥3)" value={qualifiedCalls} sub={rangeLabel} />
        <MetricCard label="Meetings set" value={rangeMeetings} sub={rangeLabel} />
        <MetricCard label="Passed to AM" value={rangePassedToAM} sub={rangeLabel} />
        <MetricCard label="Active deals" value={activeDeals} sub="In pipeline" />
        <MetricCard label="Reminders due" value={overdueReminders} sub="Today & overdue" valueColor={overdueReminders > 0 ? '#E24B4A' : undefined} />
        <MetricCard label="DB contacts" value={allContacts.length} sub="Total database" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
        <Card>
          <SectionTitle>Qualified calls per agent (BANT ≥ 3) — {rangeLabel}</SectionTitle>
          <ResponsiveContainer width="100%" height={170}>
            <BarChart data={chartData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
              <XAxis dataKey="agent" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="calls" radius={[4, 4, 0, 0]}>
                {chartData.map((entry, i) => (
                  <rect key={i} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card>
          <SectionTitle>Pipeline by stage</SectionTitle>
          {Object.entries(stages).length > 0 ? Object.entries(stages).map(([s, n]) => (
            <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderBottom: '0.5px solid rgba(0,0,0,0.07)' }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: STAGE_COLORS[s] || '#888', flexShrink: 0 }} />
              <div style={{ flex: 1, fontSize: 12 }}>{s}</div>
              <div style={{ fontSize: 11, color: '#888' }}>{n} deal{n > 1 ? 's' : ''}</div>
            </div>
          )) : <EmptyState>No active deals yet</EmptyState>}
        </Card>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {agents.map(a => (
          <AgentCard key={a} agent={a} callLogs={callLogs} today={today} onNameClick={setDetailAgent}
            rangeFrom={rangeFrom} rangeTo={rangeTo} rangeLabel={rangeLabel} />
        ))}
      </div>

      {/* Weekly Progress Tracker */}
      <div style={{ marginTop: '1.25rem' }}>
        <Card>
          <SectionTitle>{showHistory ? `Weekly Meeting Progress — History (sebelum ${kpiStartLabel})` : 'Weekly Meeting Progress'}</SectionTitle>
          {(() => {
            const weeks = getWeeks(callLogs, kpiMode)
            const recentWeeks = weeks.slice(-6) // Show last 6 weeks
            return (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid rgba(0,0,0,0.1)' }}>
                      <th style={{ padding: '6px 8px', textAlign: 'left', color: '#888', fontWeight: 600 }}>Agent</th>
                      {recentWeeks.map(w => (
                        <th key={w} style={{ padding: '6px 8px', textAlign: 'center', color: '#888', fontWeight: 500, fontSize: 10 }}>
                          {new Date(w).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {agents.map(a => (
                      <tr key={a} style={{ borderBottom: '0.5px solid rgba(0,0,0,0.07)' }}>
                        <td style={{ padding: '6px 8px', fontWeight: 600 }}>{a}</td>
                        {recentWeeks.map(weekStart => {
                          const weekEnd = new Date(new Date(weekStart).getTime() + 6 * 86400000).toISOString().slice(0, 10)
                          const meetings = callLogs.filter(r =>
                            r.agent === a && r.meeting_set === 'Yes' && r.call_date >= weekStart && r.call_date <= weekEnd
                          ).length
                          const target = WEEKLY_MEETING_TARGETS[a]
                          const achieved = meetings >= target
                          const currentWeek = weekStart === getWeekStart(today)
                          return (
                            <td key={weekStart} style={{ padding: '6px 8px', textAlign: 'center' }}>
                              <span style={{
                                display: 'inline-block', minWidth: 28, padding: '2px 6px', borderRadius: 6, fontWeight: 600, fontSize: 11,
                                background: currentWeek ? '#E6F1FB' : achieved ? '#E1F5EE' : meetings > 0 ? '#FEF3E2' : '#FCEBEB',
                                color: currentWeek ? '#185FA5' : achieved ? '#0F6E56' : meetings > 0 ? '#854F0B' : '#A32D2D',
                              }}>
                                {meetings}/{target}
                              </span>
                            </td>
                          )
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          })()}
          <div style={{ display: 'flex', gap: 12, marginTop: 10, fontSize: 10, color: '#888' }}>
            <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 3, background: '#E1F5EE', marginRight: 3 }}></span> Achieved</span>
            <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 3, background: '#FEF3E2', marginRight: 3 }}></span> Partial</span>
            <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 3, background: '#FCEBEB', marginRight: 3 }}></span> Missed</span>
            <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 3, background: '#E6F1FB', marginRight: 3 }}></span> Current week</span>
          </div>
        </Card>
      </div>
    </div>
  )
}
